import { InspectionFormData, InspectionModalData } from '@/types/model/inspection-modal';
import { offlineModeManager } from '@/services/offline/OfflineModeManager';
import { inspectionDataService } from './InspectionDataService';
import { databaseCachingService } from '@/services/offline/DatabaseCachingService';
import { InspectionGroup, Inspection } from '@/types/model/inspection';
import lookupDataService from './LookupDataService';
import { FileResponse, InspectionLineItemResponse } from '@/types/response/InspectionResultResponses';
import { GroupsResponse } from '@/types/response/lookupResponses';
import { contractDataService } from './ContractDataService';
import { apiService } from '../ApiService';
import auth from '../UserAuthService';

interface OfflineStatus {
  isOnline: boolean;
  isOfflineMode: boolean;
}

class DataManager {
  private currentStatus: OfflineStatus = {
    isOnline: true,
    isOfflineMode: false,
  };

  constructor() {
    this.initialize();
  }

  initialize() {
    console.log('DataManager initialized with status:', this.currentStatus);
    const unsubscribeOffline = offlineModeManager.addListener((status) => {
      this.updateOfflineStatus(status.isOfflineMode, status.isOnline);
    });

    return () => {
      unsubscribeOffline();
    };
  }

  // Method to be called by OfflineContext when offline mode changes
  updateOfflineStatus(isOfflineModeEnabled: boolean, isActuallyOnline: boolean): void {
    console.log('📱 Updating offline status...', { isOfflineModeEnabled, isActuallyOnline });
    
    this.currentStatus.isOnline = isActuallyOnline;
    this.currentStatus.isOfflineMode = isOfflineModeEnabled;
  }

  
  public async saveInspection(inspectionData: InspectionModalData, inspectionFormData: InspectionFormData, originalFormData: InspectionFormData): Promise<void> {
    console.log('Save Inspection initiated ... data :', inspectionData, inspectionFormData, originalFormData);

      if (this.currentStatus.isOfflineMode) {
      await inspectionDataService.updateInspectionCache(
        inspectionData,
        inspectionFormData
      );
    } else {
      await inspectionDataService.saveInspection(
        inspectionData,
        inspectionFormData,
        originalFormData
      );
    }
  }

  public async cacheInspections(groupedData: InspectionGroup[]): Promise<boolean> {
    try {
      if (this.currentStatus.isOfflineMode) {
        console.log('Caching not allowed in offline mode.');
        throw new Error('Cannot cache data while in offline mode');
      }

      const currentContract = auth.getCurrentContract();
      if (!currentContract) {
        throw new Error('No current contract selected');
      }
      await databaseCachingService.cacheInspections(groupedData, currentContract);
      return true;
    } catch (error) {
      console.error('Error persisting all data:', error);
      return false;
    }
  }
   
  public async getGroupedInspectionData(groupIds: number[] = [], siteIds: number[] = [], areaIds: number[] = [], from?: string, to?: string): Promise<InspectionGroup[]> {
    try {
      if (this.currentStatus.isOfflineMode) {
        const cachedData = await inspectionDataService.getCachedContractInspectionGroups();
        // console.log('Cached inspection groups retrieved:', cachedData);
        return cachedData;
      } else {
        const liveData = await inspectionDataService.getGroupedInspectionData(groupIds, siteIds, areaIds, from, to);
        // console.log('Live inspection groups retrieved:', liveData);
        return liveData;
      }
    } catch (error) {
      console.error('Error fetching contract inspection groups:', error);
      throw error;
    }
  }

  public async getInspections(ids: number[]): Promise<Inspection[]> {
    try {
      if (this.currentStatus.isOfflineMode) { 
        console.log('Fetching inspections from cache for IDs:', ids);
        const cachedInspections = await inspectionDataService.getCachedInspections(ids);
        return cachedInspections; // Return cached inspections in offline mode
      } else {
        const liveInspections = await inspectionDataService.getInspections(ids);
        return liveInspections;
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
      throw error;
    }
  }

  public async getContractInspection(inspectionResultId: number): Promise<InspectionLineItemResponse> {
    try {
      if (this.currentStatus.isOfflineMode) {
        return await inspectionDataService.getCachedContractInspection(inspectionResultId);
      }

      return await inspectionDataService.getContractInspection(inspectionResultId);
    } catch (error) {
      console.error('Error fetching inspection details:', error);
      throw error;
    }
  }

  public async getContractExistingResources(): Promise<FileResponse[]> {
    try {
      if (this.currentStatus.isOfflineMode) {
        const cachedData = [] as FileResponse[]; // Return empty array in offline mode
        return cachedData;
      } else {
        const liveData = lookupDataService.getExistingResources();
        return liveData || [];
      }
    } catch (error) {
      console.error('Error fetching contract files:', error);
      throw error;
    }
  }

 public async getContracts(): Promise<GroupsResponse[]> {
    try {
      if (this.currentStatus.isOfflineMode) {
        const cachedGroups = await contractDataService.getCachedContracts();
        return cachedGroups;
      } else {
        const contracts = await apiService.getContracts();
        return contracts;
      }
    } catch (error) {
      console.error('Error fetching contract groups:', error);
      throw error;
    }
  } 
}

// Export singleton instance
export const dataManager = new DataManager();
export default dataManager;